import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Heading,
    Page,
    PageContent,
    Spinner,
    Text,
} from 'grommet';
import { redirect, useLoaderData } from 'react-router-dom';
import {
    API,
    AppConstants,
    UserService,
    hasToken,
    hasUserId,
    userIsAdmin,
    userCanViewWelcomeCalls,
} from '@enrich/shared-lib';

export async function welcomeCallsLoader() {
    if (!hasToken() || !hasUserId()) {
        return redirect('/login');
    }

    const userId = localStorage.getItem(AppConstants.cacheKeys.userId);
    try {
        const user = await new UserService().getUser(userId);
        if (!user || !userCanViewWelcomeCalls(user)) {
            return redirect('/');
        }
        return user;
    } catch {
        return redirect('/login');
    }
}

const formatWhen = (iso) => {
    if (!iso) return 'TBD';
    return new Date(iso).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const studentName = (contact) => {
    if (!contact) return 'Unknown student';
    const name = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
    return name || contact.email || 'Unknown student';
};

// The coach relation is null whenever the Calendly host has no matching
// welcome-call-coach record — resolveWelcomeCallCoach returns null and alerts
// Slack — so the row degrades to a placeholder instead of breaking.
const coachName = (welcomeCallCoach) => {
    if (!welcomeCallCoach) return 'Unassigned';
    return welcomeCallCoach.fullName || welcomeCallCoach.email || 'Unassigned';
};

// `cells` is already built for the viewer, so every row — header included —
// shares one width and the columns line up.
const Row = ({ cells }) => (
    <Box
        direction="row-responsive"
        justify="between"
        align="center"
        pad="small"
        border="bottom"
        gap="small">
        {cells.map(({ key, content }) => (
            <Box key={key} basis={cells.length === 4 ? '1/4' : '1/3'}>
                {content}
            </Box>
        ))}
    </Box>
);

const HeaderCell = ({ label }) => (
    <Text size="small" weight="bold" color="dark-3">
        {label}
    </Text>
);

const HeaderRow = ({ showCoach }) => (
    <Row
        cells={[
            { key: 'student', content: <HeaderCell label="Student" /> },
            { key: 'when', content: <HeaderCell label="When" /> },
            ...(showCoach
                ? [
                      {
                          key: 'coach',
                          content: <HeaderCell label="Assigned Coach" />,
                      },
                  ]
                : []),
            { key: 'join', content: <HeaderCell label="Join" /> },
        ]}
    />
);

const CallRow = ({ meeting, showJoin, showCoach }) => (
    <Row
        cells={[
            {
                key: 'student',
                content: (
                    <Text weight={500}>{studentName(meeting.contact)}</Text>
                ),
            },
            {
                key: 'when',
                content: <Text>{formatWhen(meeting.meetingTime)}</Text>,
            },
            ...(showCoach
                ? [
                      {
                          key: 'coach',
                          content: (
                              <Text>{coachName(meeting.welcomeCallCoach)}</Text>
                          ),
                      },
                  ]
                : []),
            {
                key: 'join',
                content:
                    showJoin && meeting.joinUrl ? (
                        <Anchor
                            href={meeting.joinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            label="Join call"
                        />
                    ) : (
                        <Text color="dark-4">—</Text>
                    ),
            },
        ]}
    />
);

const Section = ({ title, meetings, emptyText, showJoin, showCoach }) => (
    <Box margin={{ top: 'medium' }} gap="xsmall">
        <Heading level="3" margin={{ bottom: 'xsmall' }}>
            {title}
        </Heading>
        {meetings.length === 0 ? (
            <Text color="dark-4">{emptyText}</Text>
        ) : (
            <>
                <HeaderRow showCoach={showCoach} />
                {meetings.map((m) => (
                    <CallRow
                        key={m.id}
                        meeting={m}
                        showJoin={showJoin}
                        showCoach={showCoach}
                    />
                ))}
            </>
        )}
    </Box>
);

export const WelcomeCalls = () => {
    const user = useLoaderData();
    const [meetings, setMeetings] = useState(null);

    // Only an admin gets every coach's calls back, so only they need to be
    // told who owns each one.
    const showCoach = userIsAdmin(user);

    useEffect(() => {
        let isActive = true;
        (async () => {
            try {
                const response = await API.get(
                    '/api/welcome-call-coaches/me/meetings',
                );
                if (isActive) setMeetings(response.data?.data ?? []);
            } catch (error) {
                console.log(error);
                if (isActive) setMeetings([]);
                // 401s are handled globally by the Axios instance (redirects to
                // /login), so reaching here means a non-auth failure — safe to
                // show empty state rather than crashing the page.
            }
        })();
        return () => {
            isActive = false;
        };
    }, []);

    const now = Date.now();
    const upcoming = (meetings ?? [])
        .filter(
            (m) => !m.meetingTime || new Date(m.meetingTime).getTime() >= now,
        )
        .sort((a, b) => {
            if (!a.meetingTime) return 1;
            if (!b.meetingTime) return -1;
            return new Date(a.meetingTime) - new Date(b.meetingTime);
        });
    const past = (meetings ?? [])
        .filter((m) => m.meetingTime && new Date(m.meetingTime).getTime() < now)
        .sort((a, b) => new Date(b.meetingTime) - new Date(a.meetingTime));

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Heading level="2">Welcome Calls</Heading>
                {meetings === null ? (
                    <Box pad="medium" align="start">
                        <Spinner />
                    </Box>
                ) : (
                    <Box>
                        <Section
                            title="Upcoming"
                            meetings={upcoming}
                            emptyText="No upcoming welcome calls yet."
                            showJoin
                            showCoach={showCoach}
                        />
                        <Section
                            title="Past"
                            meetings={past}
                            emptyText="No past welcome calls."
                            showJoin={false}
                            showCoach={showCoach}
                        />
                    </Box>
                )}
            </PageContent>
        </Page>
    );
};
